'use client';

import type { AppProps } from 'next/app';
import React from 'react';
import { useToggle, useCookie, useLocalStorage } from 'react-use';
import AuthModal from '../components/AuthModal';
import SubscribeModal from '../components/SubscribeModal';
import { SessionProvider } from 'next-auth/react';
import { useSession } from 'next-auth/react';
import type { Session } from 'modules/auth/interfaces';
import { useRouter } from 'next/router';

import useNotifier from 'modules/Notification/hooks/useNotifier';
import useUrl from 'modules/common/hooks/useUrl';
import { useTranslation } from 'modules/I18n';
import { trackAuthOpenModal, trackAuthCloseModal } from 'modules/Analytics';

export const AuthContext = React.createContext({
  modalOpened: false,
  toggleModal: () => {},
  session: null as Session | null,
});

const handledErrors = [
  'Signin',
  'OAuthSignin',
  'OAuthCallback',
  'OAuthCreateAccount',
  'EmailCreateAccount',
  'Callback',
  'OAuthAccountNotLinked',
  'EmailSignin',
  'CredentialsSignin',
  'NotConnected',
];

// we need to extract this in order to have access to the session through useSession
const ContextProviderWithSession = ({ value, children }: any) => {
  const { data: session } = useSession();
  const { queryParams, removeQueryParam, push } = useUrl();
  const { pathname } = useRouter();
  const { notify } = useNotifier();
  const { t } = useTranslation();
  const [redirectAfterLogin, setRedirectAfterLogin, removeRedirectAfterLogin] = useCookie('app-redirect');

  const { error, callbackUrl, redirect } = queryParams;

  // Display error in notifier and remove it
  React.useEffect(() => {
    if (error && handledErrors.includes(error)) {
      const translatedKey = `auth.common:error.${error}`;
      const translatedText = t(translatedKey);

      const message =
        translatedKey === translatedText ? (
          error
        ) : (
          <>
            {translatedText} <small className="text-gray-500">{error}</small>
          </>
        );

      notify('error', message, { toastId: error });
      removeQueryParam('error', undefined, { shallow: true });
    }
  }, [error, notify, removeQueryParam, t]);

  React.useEffect(() => {
    if (callbackUrl) {
      push(callbackUrl);
    }
  }, [push, callbackUrl]);

  React.useEffect(() => {
    if (redirect) {
      setRedirectAfterLogin(redirect);
      removeQueryParam('redirect', undefined, { shallow: true });
    }
  }, [redirect, removeQueryParam, setRedirectAfterLogin]);

  React.useEffect(() => {
    if (session) {
      if (redirectAfterLogin) {
        removeRedirectAfterLogin();
        push(redirectAfterLogin);
      }
    }
  }, [pathname, session, redirectAfterLogin, removeRedirectAfterLogin, push]);

  return (
    <AuthContext.Provider
      value={{
        ...value,
        session,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default function AuthProvider({ pageProps: { session }, children }: AppProps['pageProps'] & any) {
  const [modalOpened, toggleModal] = useToggle(false);
  const [subscribeModalOpened, toggleSubscribeModal] = useToggle(false);
  const [subscribeModalShownOnce, toggleSubscribeModalShownOnce] = useLocalStorage('sc-subscribe-modal-shown', false);

  const toggleModalWithAnalytics = (val: boolean = true) => {
    if (val) {
      trackAuthOpenModal();
    } else {
      trackAuthCloseModal();
    }
    toggleModal(val);
  };

  React.useEffect(() => {
    if (!subscribeModalShownOnce) {
      setTimeout(() => {
        // toggleSubscribeModal(true);
        // toggleSubscribeModalShownOnce(true);
      }, 60 * 4 * 1000);
    }
  }, [subscribeModalShownOnce, toggleModal, toggleSubscribeModalShownOnce, toggleSubscribeModal]);

  return (
    <SessionProvider session={session}>
      <AuthModal open={modalOpened} onClose={() => toggleModalWithAnalytics(false)} />
      <SubscribeModal
        open={subscribeModalOpened}
        onClose={() => toggleSubscribeModal(false)}
        onAuth={() => {
          toggleSubscribeModal(false);
          toggleModal(true);
        }}
      />
      <ContextProviderWithSession value={{ modalOpened, toggleModal: toggleModalWithAnalytics }}>{children}</ContextProviderWithSession>
    </SessionProvider>
  );
}
